import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { PlusCircle, X, FileText, Tag, Folder } from 'lucide-react'

export default function BlogPostCreate({ isOpen, onClose, onSuccess }) {
  const { accessToken } = useAuth()
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: '',
    tags: ''
  })
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState('')

  const categories = [
    'Events',
    'Culture',
    'Heritage',
    'Traditions',
    'Stories',
    'News',
    'Community',
    'History',
    'Celebrations',
    'Other'
  ]

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Please fill in title and content')
      return
    }

    setPublishing(true)
    setError('')

    try {
      const postData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        excerpt: formData.excerpt.trim() || formData.content.substring(0, 200) + '...',
        category: formData.category || 'General',
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        isPublished: true
      }

      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/blog`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(postData)
      })

      if (!response.ok) {
        throw new Error('Failed to create blog post')
      }

      const data = await response.json()
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to create blog post')
      }

      // Success!
      onSuccess && onSuccess(data.data)
      handleClose()
      
    } catch (err) {
      console.error('Create post error:', err)
      setError(err.message || 'Failed to create blog post')
    } finally {
      setPublishing(false)
    }
  }

  const handleClose = () => {
    setFormData({ title: '', content: '', excerpt: '', category: '', tags: '' })
    setError('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <PlusCircle className="w-6 h-6 text-primary-600" />
              <span>Write a Blog Post</span>
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Post Title *
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="title"
                  name="title"
                  type="text"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  className="input-field pl-10"
                  placeholder="e.g., Celebrating Our Annual Clan Gathering"
                />
              </div>
            </div>

            {/* Category and Tags Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <div className="relative">
                  <Folder className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (optional)
                </label>
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="tags"
                    name="tags"
                    type="text"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="e.g., celebration, community, tradition"
                  />
                </div>
              </div>
            </div>

            {/* Excerpt */}
            <div>
              <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-2">
                Excerpt (optional)
              </label>
              <textarea
                id="excerpt"
                name="excerpt"
                rows={3}
                value={formData.excerpt}
                onChange={handleInputChange}
                className="input-field"
                placeholder="A brief summary of your post (will be auto-generated if left empty)"
              />
              <p className="text-xs text-gray-500 mt-1">
                This will appear in the blog listing. If empty, we'll use the first 200 characters of your content.
              </p>
            </div>

            {/* Content */}
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                Content *
              </label>
              <textarea
                id="content"
                name="content"
                required
                rows={12}
                value={formData.content}
                onChange={handleInputChange}
                className="input-field"
                placeholder="Share your story, traditions, insights, or news with the Kapcheboin community..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Share stories, traditions, cultural insights, community news, or anything that connects us to our heritage.
              </p>
            </div>

            {/* Submit Buttons */}
            <div className="flex space-x-4 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={publishing}
                className="flex-1 btn-primary py-3 flex items-center justify-center space-x-2"
              >
                {publishing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Publishing...</span>
                  </>
                ) : (
                  <>
                    <PlusCircle className="w-5 h-5" />
                    <span>Publish Post</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}